import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Upload, Sparkles, Camera, Loader2, Flame, Star, Zap, Check,
  Eye, Users, TrendingUp, Dna, Shield, RefreshCw
} from "lucide-react";
import CreatorDNAQuiz from "../components/CreatorDNAQuiz";
import LeadCaptureCard from "../components/LeadCaptureCard";
import EarlyBirdStrip from "../components/EarlyBirdStrip";
import { ViralOutput } from "../components/creators/ViralOutput";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES = [
  { id: "Basic", price: "3,999", color: "#10B981" },
  { id: "Classic", price: "5,999", color: "#3B82F6" },
  { id: "Premium", price: "8,999", color: "#7C3AED" },
  { id: "Luxury", price: "14,999", color: "#E83E8C" },
];

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function CreatorsPage() {
  const [image, setImage] = useState(null);
  const [imageB64, setImageB64] = useState("");
  const [partyStyle, setPartyStyle] = useState("premium");
  const [occasion, setOccasion] = useState("Birthday");
  const [selectedPkg, setSelectedPkg] = useState("Premium");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [showDNA, setShowDNA] = useState(false);
  const [dnaPersonality, setDnaPersonality] = useState(null);
  const [dnaAnswers, setDnaAnswers] = useState(null);
  const [remaining, setRemaining] = useState(3);
  const [limitHit, setLimitHit] = useState(false);
  const [teamMode, setTeamMode] = useState(false);
  const [teamCode, setTeamCode] = useState("");
  const [showTeamLogin, setShowTeamLogin] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [genError, setGenError] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // Resize + JPEG-encode the upload client-side. Mobile cameras produce
  // 3-10 MB images (or HEIC on iPhone) that:
  //   • blow past API gateway body-size limits when base64-inflated 33%
  //   • take so long the browser/proxy times out before the LLM responds
  //   • are rejected outright if HEIC (Gemini wants JPEG/PNG)
  // 1600px on the longest edge at JPEG-85 keeps the input gorgeous and
  // typically under 800 KB → comfortably under any timeout/limit.
  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read the image"));
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error("Couldn't decode the image — try another photo"));
      img.onload = () => {
        const MAX_EDGE = 1600;
        const ratio = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Walk quality down until the b64 payload is < ~1.5 MB
        let quality = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > 1_500_000 && quality > 0.5) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        resolve({
          previewUrl: dataUrl,
          b64: dataUrl.split(",")[1],
          finalKb: Math.round(dataUrl.length / 1024),
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  // POST with an explicit timeout — mobile fetch otherwise hangs forever
  // when the gateway silently drops the connection.
  const callGenerate = async (payload, timeoutMs = 120_000) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${API_URL}/api/creators/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      });
      let data = {};
      try { data = await res.json(); } catch { /* non-JSON body */ }
      return { res, data };
    } finally {
      clearTimeout(timer);
    }
  };

  // Check remaining generations on load
  React.useEffect(() => {
    if (teamMode) { setRemaining(999); setLimitHit(false); return; }
    fetch(`${API_URL}/api/creators/remaining`)
      .then(r => r.json())
      .then(d => { setRemaining(d.remaining); if (d.remaining <= 0) setLimitHit(true); })
      .catch(() => {});
  }, [result, teamMode]);

  const handleTeamLogin = async () => {
    setTeamError("");
    try {
      const res = await fetch(`${API_URL}/api/creators/verify-team`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_code: teamCode }),
      });
      if (res.ok) {
        setTeamMode(true);
        setShowTeamLogin(false);
        setLimitHit(false);
        setRemaining(999);
      } else {
        setTeamError("Invalid team code");
      }
    } catch { setTeamError("Connection error"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGenError("");
    setImage(URL.createObjectURL(file));
    try {
      const { previewUrl, b64, finalKb } = await compressImage(file);
      setImage(previewUrl);
      setImageB64(b64);
      console.log(`[Creator Studio] image compressed → ${finalKb} KB`);
    } catch (err) {
      console.error("[Creator Studio] compression failed:", err);
      setGenError(err?.message || "Couldn't process this image. Try a JPEG or PNG.");
      setImageB64("");
      setImage(null);
    }
  };

  const handleGenerate = async () => {
    if (!imageB64) return;
    setGenerating(true);
    setGenError("");
    try {
      const payload = {
        image_b64: imageB64,
        party_style: partyStyle,
        occasion: occasion,
        package_name: selectedPkg,
      };
      if (teamMode) payload.team_code = teamCode;
      const { res, data } = await callGenerate(payload);
      if (res.ok) {
        setResult(data);
      } else if (res.status === 429) {
        setLimitHit(true);
        setRemaining(0);
      } else if (res.status === 503) {
        setGenError("AI is temporarily unavailable. Please try again in a moment.");
      } else if (res.status === 413 || res.status === 422) {
        setGenError("This image is too big to process. Please pick a smaller photo.");
      } else {
        setGenError(data?.detail || `Couldn't generate (HTTP ${res.status}). Please retry.`);
      }
    } catch (err) {
      // Real network failure (timeout, offline, DNS, CORS).
      console.error("Creator transform failed:", err);
      const isTimeout = err?.name === "AbortError";
      setGenError(
        isTimeout
          ? "Generation took too long — please try again with a smaller image or fewer details."
          : "Network error — please check your connection and try again."
      );
    }
    setGenerating(false);
  };

  const STYLES = [
    { id: "premium", label: "Premium", emoji: "✨" },
    { id: "bollywood", label: "Bollywood", emoji: "🪩" },
    { id: "minimal", label: "Minimal", emoji: "🤍" },
    { id: "neon", label: "Neon", emoji: "💜" },
    { id: "romantic", label: "Romantic", emoji: "🌹" },
  ];

  const OCCASIONS = ["Birthday", "Anniversary", "Baby Shower", "Housewarming", "Just Because"];

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white" data-testid="creators-page">
      {/* CSS */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
      `}</style>

      <EarlyBirdStrip />

      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0A0A0F]/90 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-white/40 text-xs hover:text-white">DoozyGo</Link>
          <span className="text-white/60 text-xs font-medium flex items-center gap-1"><Flame size={12} className="text-[#E83E8C]" /> Creator Studio</span>
          {teamMode ? (
            <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1"><Check size={10} /> Team</span>
          ) : (
            <button onClick={() => setShowTeamLogin(true)} className="text-[10px] text-white/30 hover:text-white/60" data-testid="team-login-toggle">Team</button>
          )}
        </div>
        {/* Team mode banner */}
        {teamMode && (
          <div className="bg-emerald-500/10 border-b border-emerald-500/20 text-center py-1.5 px-4">
            <span className="text-[10px] text-emerald-400 font-medium">Team Test Mode — Unlimited generations active</span>
          </div>
        )}
      </div>

      {/* Team Login Modal */}
      {showTeamLogin && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowTeamLogin(false)}>
          <div className="bg-[#1A1A2E] rounded-2xl p-6 max-w-xs w-full space-y-4 border border-white/10" onClick={e => e.stopPropagation()} data-testid="team-login-modal">
            <div className="text-center">
              <Shield size={24} className="text-[#E83E8C] mx-auto mb-2" />
              <h3 className="text-sm font-bold text-white">Team Access</h3>
              <p className="text-[10px] text-white/40 mt-1">Enter team code for unlimited testing</p>
            </div>
            <input value={teamCode} onChange={e => setTeamCode(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleTeamLogin()}
              placeholder="Enter team code"
              type="password"
              className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-sm text-white placeholder-white/30 text-center"
              data-testid="team-code-input" />
            {teamError && <p className="text-[10px] text-red-400 text-center">{teamError}</p>}
            <button onClick={handleTeamLogin} className="w-full py-2.5 rounded-xl bg-[#E83E8C] text-white text-xs font-bold" data-testid="team-code-submit">
              Activate Team Mode
            </button>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {!result ? (
          <>
            {/* Hero */}
            <div className="text-center py-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E83E8C]/20 border border-[#E83E8C]/30 mb-4">
                <TrendingUp size={12} className="text-[#E83E8C]" />
                <span className="text-[10px] font-bold text-[#E83E8C]">CREATOR PROGRAM</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white via-[#E83E8C] to-[#FF9F1C] bg-clip-text text-transparent leading-tight">
                Turn Your House Into<br />Viral Party Content
              </h1>
              <p className="text-white/40 text-xs mt-3 max-w-xs mx-auto">
                Upload your room. Get an insane transformation. Get a ready-made reel plan. Post. Go viral.
              </p>
            </div>

            {/* Upload — Camera + Gallery options */}
            <div className={`rounded-2xl border-2 border-dashed transition-all ${image ? "border-[#E83E8C]/40 bg-[#E83E8C]/5" : "border-white/10 hover:border-white/20"} p-6`}>
              {image ? (
                <div className="text-center space-y-3">
                  <img src={image} alt="Your room" className="max-h-64 mx-auto rounded-xl" />
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => cameraRef.current?.click()} className="text-xs text-white/40 hover:text-white flex items-center gap-1"><Camera size={12} /> Retake</button>
                    <button onClick={() => fileRef.current?.click()} className="text-xs text-white/40 hover:text-white flex items-center gap-1"><Upload size={12} /> Gallery</button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 space-y-4">
                  <Camera size={40} className="text-white/20 mx-auto" />
                  <p className="text-sm font-medium text-white/60">Snap your room or pick from gallery</p>
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => cameraRef.current?.click()}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E83E8C] text-white text-xs font-bold" data-testid="open-camera-btn">
                      <Camera size={14} /> Open Camera
                    </button>
                    <button onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold" data-testid="open-gallery-btn">
                      <Upload size={14} /> Gallery
                    </button>
                  </div>
                </div>
              )}
              {/* Camera input */}
              <input ref={cameraRef} type="file" className="hidden" accept="image/*" capture="environment" onChange={handleUpload} data-testid="creator-camera" />
              {/* Gallery input */}
              <input ref={fileRef} type="file" className="hidden" accept="image/*" onChange={handleUpload} data-testid="creator-upload" />
            </div>

            {/* Package Selection */}
            <div>
              <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2">Package</p>
              <div className="grid grid-cols-2 gap-2">
                {PACKAGES.map(pkg => (
                  <button key={pkg.id} onClick={() => setSelectedPkg(pkg.id)}
                    className={`p-3 rounded-xl text-left transition-all ${
                      selectedPkg === pkg.id ? "bg-[#E83E8C]/20 border border-[#E83E8C]" : "bg-white/5 border border-white/10 hover:border-white/20"
                    }`} data-testid={`creator-pkg-${pkg.id}`}>
                    <p className="text-xs font-bold" style={{ color: pkg.color }}>{pkg.id}</p>
                    <p className="text-sm font-bold text-white">Rs {pkg.price}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Style selector */}
            <div>
              <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2">Party Vibe</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {STYLES.map(s => (
                  <button key={s.id} onClick={() => setPartyStyle(s.id)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                      partyStyle === s.id ? "bg-[#E83E8C] text-white" : "bg-white/5 text-white/50 hover:bg-white/10"
                    }`} data-testid={`style-${s.id}`}>
                    {s.emoji} {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Occasion */}
            <div>
              <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-2">Occasion</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {OCCASIONS.map(o => (
                  <button key={o} onClick={() => setOccasion(o)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                      occasion === o ? "bg-white text-[#0A0A0F]" : "bg-white/5 text-white/50 hover:bg-white/10"
                    }`} data-testid={`occasion-${o}`}>
                    {o}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate */}
            {genError && (
              <div
                className="bg-red-500/10 border border-red-500/30 text-red-200 rounded-2xl px-4 py-3 text-xs flex items-start justify-between gap-3"
                data-testid="creator-gen-error"
              >
                <span>{genError}</span>
                <button
                  onClick={() => setGenError("")}
                  className="text-red-300 hover:text-red-100 text-xs font-bold flex-shrink-0"
                  data-testid="creator-gen-error-dismiss"
                >
                  ✕
                </button>
              </div>
            )}
            {limitHit && !teamMode ? (
              <div className="space-y-4 py-4 px-3 bg-white/5 rounded-2xl border border-[#E83E8C]/20">
                <div className="text-center space-y-2">
                  <Zap size={28} className="text-[#E83E8C] mx-auto" />
                  <p className="text-base font-bold text-white">Out of free transformations</p>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    You've used all 2 free generations. Drop your details below and our team will WhatsApp you a personal access link — or enter your team code if you have one.
                  </p>
                </div>
                <LeadCaptureCard
                  source="creator_studio"
                  variant="dark"
                  extra={{
                    package_name: selectedPkg,
                    party_style: partyStyle,
                    occasion: occasion,
                    gate_reason: "free_limit_reached",
                  }}
                  headline="Unlock unlimited creator-style transformations"
                  subheadline="Drop your number — get a personal access link + a free quote within 24h."
                  cta="Send My Access Link"
                />
                <button onClick={() => setShowTeamLogin(true)}
                  className="block mx-auto text-[11px] text-[#E83E8C] underline hover:text-[#E83E8C]/80" data-testid="team-unlock-btn">
                  Team member? Enter code to unlock
                </button>
              </div>
            ) : (
              <>
                <button onClick={handleGenerate} disabled={!imageB64 || generating}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-base disabled:opacity-30 hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-[0_0_40px_rgba(232,62,140,0.3)]"
                  data-testid="generate-viral-btn">
                  {generating ? (
                    <><Loader2 size={18} className="animate-spin" /> Creating your viral content...</>
                  ) : (
                    <><Sparkles size={18} /> Turn My Room Into Viral Content</>
                  )}
                </button>
                {remaining < 2 && (
                  <p className="text-[10px] text-center text-amber-400">{remaining} free generation{remaining !== 1 ? "s" : ""} remaining</p>
                )}
              </>
            )}

            {/* Social proof */}
            <div className="flex items-center justify-center gap-4 text-[10px] text-white/30">
              <span className="flex items-center gap-1"><Eye size={10} /> 2.4K+ transformations</span>
              <span className="flex items-center gap-1"><Users size={10} /> 850+ creators</span>
              <span className="flex items-center gap-1"><TrendingUp size={10} /> 12M+ views generated</span>
            </div>

            {/* Show DNA result badge if applied */}
            {dnaPersonality && !showDNA && (
              <div className="bg-[#E83E8C]/10 border border-[#E83E8C]/20 rounded-xl p-3 flex items-center gap-3">
                <Zap size={16} className="text-[#E83E8C]" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-white">Party DNA: {dnaPersonality.personality_name}</p>
                  <p className="text-[10px] text-white/40">Vibe & occasion auto-set. Upload your room to create content!</p>
                </div>
              </div>
            )}

            {/* Party DNA — Embedded in Creator Theme */}
            <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden" data-testid="creator-dna-section">
              {!showDNA ? (
                <button onClick={() => setShowDNA(true)}
                  className="w-full p-5 text-center hover:bg-white/5 transition-all" data-testid="party-dna-link">
                  <Zap size={20} className="text-[#E83E8C] mx-auto mb-1" />
                  <p className="text-xs font-bold text-white/70">Not sure what vibe to go for?</p>
                  <p className="text-sm font-bold bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] bg-clip-text text-transparent mt-0.5">Discover Your Party DNA →</p>
                </button>
              ) : (
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-[#E83E8C] uppercase tracking-wider">Party DNA Quiz</p>
                    <button onClick={() => setShowDNA(false)} className="text-[10px] text-white/30 hover:text-white/50">Close</button>
                  </div>
                  <CreatorDNAQuiz onResult={(personality, answers) => {
                    setDnaPersonality(personality);
                    setDnaAnswers(answers);
                    // Auto-set party style from DNA vibe
                    const vibeMap = { chill: "minimal", wild: "bollywood", elegant: "premium", themed: "neon", minimal: "minimal" };
                    if (answers?.vibe && vibeMap[answers.vibe]) setPartyStyle(vibeMap[answers.vibe]);
                    if (answers?.occasion) setOccasion(answers.occasion);
                    // Close quiz and scroll to upload
                    setShowDNA(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }} />
                  {dnaPersonality && (
                    <p className="text-[10px] text-emerald-400 text-center mt-2">
                      Vibe & occasion auto-set from your DNA result!
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Viral Output */
          <ViralOutput
            transformedImage={result.transformed_image}
            originalImage={image}
            viralContent={result.viral_content}
            onRegenerate={async (customPrompt) => {
              setGenerating(true);
              setGenError("");
              setResult(null);
              try {
                const payload = {
                  image_b64: imageB64,
                  party_style: partyStyle,
                  occasion: occasion,
                  package_name: selectedPkg,
                  custom_prompt: customPrompt || "",
                };
                if (teamMode) payload.team_code = teamCode;
                const { res, data } = await callGenerate(payload);
                if (res.ok) {
                  setResult(data);
                } else if (res.status === 429) {
                  setLimitHit(true);
                  setRemaining(0);
                } else {
                  setGenError(data?.detail || `Couldn't regenerate (HTTP ${res.status}). Please retry.`);
                }
              } catch (err) {
                console.error("Style transform failed:", err);
                setGenError(
                  err?.name === "AbortError"
                    ? "Generation took too long — please try again."
                    : "Network error — please check your connection and try again."
                );
              }
              setGenerating(false);
            }}
            generating={generating}
          />
        )}

        {/* Join Program CTA + New Transformation */}
        {result && (
          <>
            {/* Prominent "New Transformation" button */}
            <button onClick={() => { setResult(null); setImage(null); setImageB64(""); }}
              className="w-full py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white text-sm font-bold hover:bg-white/15 transition-all flex items-center justify-center gap-2"
              data-testid="new-transformation-btn">
              <RefreshCw size={16} /> Create Another Transformation
            </button>
            <JoinCreatorCTA />
          </>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-white/5">
          <p className="text-[10px] text-white/20">DoozyGo Creator Studio — Mumbai's Party Revolution</p>
        </div>
      </div>
    </div>
  );
}

// ─── Join CTA (shown after generation) ───
const JoinCreatorCTA = () => {
  const [name, setName] = useState("");
  const [insta, setInsta] = useState("");
  const [joined, setJoined] = useState(false);

  const handleJoin = async () => {
    if (!name || !insta) return;
    try {
      await fetch(`${API_URL}/api/creators/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, instagram: insta }),
      });
      setJoined(true);
    } catch (err) {
      console.error("Creator join failed:", err);
    }
  };

  if (joined) {
    return (
      <div className="text-center py-4 bg-emerald-900/20 rounded-2xl border border-emerald-500/20">
        <Check size={24} className="text-emerald-400 mx-auto mb-1" />
        <p className="text-sm font-bold text-emerald-400">You're in the Creator Program!</p>
        <p className="text-[10px] text-emerald-400/60 mt-1">Keep posting, keep winning</p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-5 space-y-3" data-testid="join-creator-cta">
      <div className="flex items-center gap-2">
        <Star size={16} className="text-amber-400" />
        <p className="text-sm font-bold text-white">Join the DoozyGo Creator Program</p>
      </div>
      <p className="text-[10px] text-white/40">Save your designs, track your posts, and win a FREE party!</p>
      <div className="flex gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
          className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-xs text-white placeholder-white/30" data-testid="creator-name" />
        <input value={insta} onChange={e => setInsta(e.target.value)} placeholder="@instagram"
          className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-xs text-white placeholder-white/30" data-testid="creator-insta" />
      </div>
      <button onClick={handleJoin} disabled={!name || !insta}
        className="w-full py-2.5 rounded-xl bg-[#E83E8C] text-white text-xs font-bold disabled:opacity-30" data-testid="join-creator-btn">
        Join Now — It's Free
      </button>
    </div>
  );
};
